import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { OpeningHourCategory } from '../../types/Location';
import { LocalizedText, emptyLocalizedText } from '../../types/LocalizedText';
import { LocalizedFieldsTabs } from '../common/LocalizedTextFields';
import { LocalizedTextDisplay } from '../common/LocalizedTextDisplay';
import { useLanguages } from '../../hooks/useLanguages';
import {
  formatLocalizedTextDisplay,
  getMissingRequiredLanguageLabels,
  hasLocalizedText,
  normalizeLocalizedText,
  serializeLocalizedText,
} from '../../utils/localizedText';
import CategoryIconPicker from '../common/CategoryIconPicker';
import { getCategoryIcon } from '../../utils/categoryIcons';

interface CategoryFormData {
  key: string;
  label: LocalizedText;
  description: LocalizedText;
  sortOrder: number;
  isActive: boolean;
  iconName: string;
  rootLevelEntry: boolean;
}

const defaultFormData: CategoryFormData = {
  key: '',
  label: emptyLocalizedText(),
  description: emptyLocalizedText(),
  sortOrder: 0,
  isActive: true,
  iconName: 'access_time',
  rootLevelEntry: false,
};

const OpeningHourCategoriesPage: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { activeLanguages, languageCodes } = useLanguages();

  const createEmptyFormData = (): CategoryFormData => ({
    key: '',
    label: emptyLocalizedText(languageCodes),
    description: emptyLocalizedText(languageCodes),
    sortOrder: 0,
    isActive: true,
    iconName: 'access_time',
    rootLevelEntry: false,
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<OpeningHourCategory | null>(null);
  const [categoryToDelete, setCategoryToDelete] = useState<OpeningHourCategory | null>(null);
  const [formData, setFormData] = useState<CategoryFormData>(defaultFormData);
  const [formError, setFormError] = useState('');
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const categoryLocalizedFields = useMemo(
    () => [
      {
        key: 'label',
        label: t('openingHourCategories.label'),
        required: true,
      },
      {
        key: 'description',
        label: t('openingHourCategories.description'),
        multiline: true,
        rows: 3,
      },
    ],
    [t]
  );

  const { data: categories = [], isLoading, error } = useQuery({
    queryKey: ['openingHourCategories', 'all'],
    queryFn: async () => {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/opening-hour-categories`,
        { params: { includeInactive: true } }
      );
      return response.data as OpeningHourCategory[];
    },
  });

  const sortedCategories = [...categories].sort(
    (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
  );

  const saveMutation = useMutation({
    mutationFn: async (data: CategoryFormData) => {
      const payload = {
        key: data.key.trim(),
        label: serializeLocalizedText(data.label, activeLanguages),
        description: hasLocalizedText(data.description)
          ? serializeLocalizedText(data.description, activeLanguages, { omitEmptyOptional: true })
          : undefined,
        sortOrder: data.sortOrder,
        isActive: data.isActive,
        iconName: data.iconName.trim() || undefined,
        rootLevelEntry: data.rootLevelEntry,
      };

      if (editingCategory) {
        return axios.put(
          `${process.env.REACT_APP_API_URL}/opening-hour-categories/${editingCategory.id}`,
          payload
        );
      }

      return axios.post(
        `${process.env.REACT_APP_API_URL}/opening-hour-categories`,
        payload
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['openingHourCategories'] });
      setDialogOpen(false);
      setEditingCategory(null);
      setFormData(createEmptyFormData());
      setFormError('');
      setNotification({
        open: true,
        message: editingCategory
          ? t('openingHourCategories.categoryUpdated')
          : t('openingHourCategories.categoryCreated'),
        severity: 'success',
      });
    },
    onError: (err: unknown) => {
      const axiosError = err as { response?: { data?: { error?: string }; status?: number } };
      if (axiosError.response?.status === 409) {
        setFormError(t('openingHourCategories.keyConflict'));
      } else {
        setFormError(
          axiosError.response?.data?.error || t('openingHourCategories.saveFailed')
        );
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return axios.delete(
        `${process.env.REACT_APP_API_URL}/opening-hour-categories/${id}`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['openingHourCategories'] });
      setDeleteDialogOpen(false);
      setCategoryToDelete(null);
      setNotification({
        open: true,
        message: t('openingHourCategories.categoryDeleted'),
        severity: 'success',
      });
    },
    onError: (err: unknown) => {
      const axiosError = err as { response?: { data?: { error?: string }; status?: number } };
      setDeleteDialogOpen(false);
      setCategoryToDelete(null);
      setNotification({
        open: true,
        message:
          axiosError.response?.status === 409
            ? t('openingHourCategories.deleteInUse')
            : axiosError.response?.data?.error || t('openingHourCategories.deleteFailed'),
        severity: 'error',
      });
    },
  });

  const openCreateDialog = () => {
    setEditingCategory(null);
    setFormData(createEmptyFormData());
    setFormError('');
    setDialogOpen(true);
  };

  const openEditDialog = (category: OpeningHourCategory) => {
    setEditingCategory(category);
    setFormData({
      key: category.key,
      label: normalizeLocalizedText(category.label, languageCodes),
      description: normalizeLocalizedText(category.description, languageCodes),
      sortOrder: category.sortOrder ?? 0,
      isActive: category.isActive !== false,
      iconName: category.iconName ?? 'access_time',
      rootLevelEntry: category.rootLevelEntry === true,
    });
    setFormError('');
    setDialogOpen(true);
  };

  const handleSave = () => {
    const missingLabels = getMissingRequiredLanguageLabels(formData.label, activeLanguages);

    if (!formData.key.trim() || missingLabels.length > 0) {
      setFormError(
        missingLabels.length > 0
          ? t('localizedText.missingRequiredLanguages', { languages: missingLabels.join(', ') })
          : t('openingHourCategories.requiredFields')
      );
      return;
    }

    if (!/^[a-z][a-zA-Z0-9]*$/.test(formData.key.trim())) {
      setFormError(t('openingHourCategories.keyFormat'));
      return;
    }

    saveMutation.mutate(formData);
  };

  const handleDeleteClick = (category: OpeningHourCategory) => {
    setCategoryToDelete(category);
    setDeleteDialogOpen(true);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">{t('openingHourCategories.errorLoading')}</Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">{t('openingHourCategories.title')}</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
          {t('openingHourCategories.addCategory')}
        </Button>
      </Box>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('openingHourCategories.key')}</TableCell>
                <TableCell align="center">{t('openingHourCategories.icon')}</TableCell>
                <TableCell sx={{ verticalAlign: 'top' }}>{t('openingHourCategories.label')}</TableCell>
                <TableCell sx={{ verticalAlign: 'top' }}>{t('openingHourCategories.description')}</TableCell>
                <TableCell align="center">{t('openingHourCategories.sortOrder')}</TableCell>
                <TableCell align="center">{t('openingHourCategories.rootMenu')}</TableCell>
                <TableCell align="center">{t('openingHourCategories.status')}</TableCell>
                <TableCell align="right">{t('locations.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedCategories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    {t('openingHourCategories.noCategories')}
                  </TableCell>
                </TableRow>
              ) : (
                sortedCategories.map((category) => (
                  <TableRow key={category.id}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {category.key}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        {getCategoryIcon(category.iconName, category.key)}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      <LocalizedTextDisplay value={category.label} languageCodes={languageCodes} />
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      <LocalizedTextDisplay
                        value={category.description}
                        languageCodes={languageCodes}
                        hideEmpty
                      />
                    </TableCell>
                    <TableCell align="center">{category.sortOrder ?? 0}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={
                          category.rootLevelEntry === true
                            ? t('common.yes')
                            : t('common.no')
                        }
                        size="small"
                        color={category.rootLevelEntry === true ? 'primary' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={
                          category.isActive !== false
                            ? t('openingHourCategories.active')
                            : t('openingHourCategories.inactive')
                        }
                        size="small"
                        color={category.isActive !== false ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => openEditDialog(category)}
                        aria-label={t('common.edit')}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteClick(category)}
                        aria-label={t('common.delete')}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCategory
            ? t('openingHourCategories.editCategory')
            : t('openingHourCategories.addCategory')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {formError && (
              <Alert severity="error">{formError}</Alert>
            )}
            <TextField
              label={t('openingHourCategories.key')}
              value={formData.key}
              onChange={(e) => setFormData({ ...formData, key: e.target.value })}
              fullWidth
              required
              disabled={!!editingCategory}
              helperText={t('openingHourCategories.keyHelp')}
            />
            <LocalizedFieldsTabs
              fields={categoryLocalizedFields}
              values={{
                label: formData.label,
                description: formData.description,
              }}
              onChange={(updatedValues) =>
                setFormData({
                  ...formData,
                  label: updatedValues.label ?? formData.label,
                  description: updatedValues.description ?? formData.description,
                })
              }
            />
            <CategoryIconPicker
              value={formData.iconName}
              onChange={(iconName) => setFormData({ ...formData, iconName })}
            />
            <TextField
              label={t('openingHourCategories.sortOrder')}
              type="number"
              value={formData.sortOrder}
              onChange={(e) =>
                setFormData({ ...formData, sortOrder: parseInt(e.target.value, 10) || 0 })
              }
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.rootLevelEntry}
                  onChange={(e) =>
                    setFormData({ ...formData, rootLevelEntry: e.target.checked })
                  }
                />
              }
              label={t('openingHourCategories.rootLevelEntry')}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label={t('openingHourCategories.active')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? t('common.loading') : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('openingHourCategories.confirmDelete')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('openingHourCategories.deleteConfirmation', {
              label: formatLocalizedTextDisplay(categoryToDelete?.label, languageCodes),
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => categoryToDelete && deleteMutation.mutate(categoryToDelete.id)}
            disabled={deleteMutation.isPending}
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification({ ...notification, open: false })}
      >
        <Alert
          severity={notification.severity}
          onClose={() => setNotification({ ...notification, open: false })}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OpeningHourCategoriesPage;
